import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useSearchParams, useNavigate, useParams } from "react-router-dom";
import {
  Package, Shirt, Palette, Plus, Trash2, Settings, Upload, Calculator,
  ChevronDown, ChevronUp, ChevronRight, AlertCircle, AlertTriangle, DollarSign, Percent, Clock,
  Eye, EyeOff, Truck, ArrowRightLeft, Check, X, Zap, FolderOpen,
  User, Lock, Search, Library, Copy, Building2, Layers,
  StickyNote, History, Timer, Calendar, FileStack, Info,
  Save, FilePlus, CheckCircle2, Mail, FileDown, ClipboardList, ExternalLink, MoreHorizontal,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { generateClientQuotePDF } from "@/utils/pdfGenerator";
import { useAuth } from "@/context/AuthContext";
import MainNav from "@/components/MainNav";
import WorkspaceLayout from "@/components/layout/WorkspaceLayout";
import ErrorBoundary, { SectionErrorBoundary } from "@/components/ErrorBoundary";
import { ColourSwatch, useAllColours } from "@/components/ColourSwatch";
import CustomerPicker from "@/components/CustomerPicker";
import BusinessNameTypeahead from "@/components/BusinessNameTypeahead";
import JobCommunicationTimeline from "@/components/JobCommunicationTimeline";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Quote components
import {
  QuotePageControls,
  JobDetailsSection,
  ClientDetailsSection,
  SupplierShippingSection,
  StickySummaryBar,
  ProductPickerModal,
  ArtworkGroupCard,
  SizeQuantityInput,
  EditableGarmentLine,
  QuantityBreakPanel,
  API,
  CLIENT_TYPES,
  PRINT_METHODS,
  SIZE_TIERS,
  BLOCKERS,
  PRINT_COLOURS,
  SUPPLIER_SHIPPING_TYPES,
  JOB_STATUSES,
  STATUS_COLORS,
  formatCurrency,
  generateId,
  getInitialJobState,
} from "@/components/quote";
import SendQuoteEmailModal from "@/components/quote/SendQuoteEmailModal";
import { VersionHistoryPanel } from "@/components/quote/VersionHistoryPanel";
import { SupplierShippingCard } from "@/components/quote/SupplierShippingCard";
import { JobDetailsCard } from "@/components/quote/JobDetailsCard";
import { GarmentsCard } from "@/components/quote/GarmentsCard";
import { PrintGroupsCard } from "@/components/quote/PrintGroupsCard";
import { QuoteSummaryPanel } from "@/components/quote/QuoteSummaryPanel";
import MethodComparisonPanel from "@/components/quote/MethodComparisonPanel";
import GarmentNotes from "@/components/quote/GarmentNotes";
import { useQuoteCalculation } from "@/components/quote/useQuoteCalculation";
import logger from "@/utils/logger";

// Helper function to calculate deadline countdown
function getDeadlineCountdown(deadline) {
  if (!deadline) return null;
  
  const now = new Date();
  const deadlineDate = new Date(deadline + "T23:59:59"); // End of deadline day
  const diffMs = deadlineDate - now;
  
  if (diffMs < 0) {
    const overdueDays = Math.ceil(Math.abs(diffMs) / (1000 * 60 * 60 * 24));
    return { overdue: true, days: overdueDays, text: `${overdueDays}d overdue` };
  }
  
  const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  
  if (days === 0) {
    return { overdue: false, days: 0, hours, text: hours <= 1 ? "Due today" : `${hours}h left` };
  } else if (days === 1) {
    return { overdue: false, days: 1, text: "Due tomorrow" };
  } else {
    return { overdue: false, days, text: `${days}d left` };
  }
}

export default function QuoteBuilder() {
  const [searchParams] = useSearchParams();
  const routeParams = useParams();
  // Support both /quote?job={id} (canonical) and /quote/:jobId (path) styles
  const jobIdParam = searchParams.get("job") || routeParams.jobId || null;
  const [jobNumber, setJobNumber] = useState(null);
  
  return (
    <ErrorBoundary 
      fallbackMessage="An error occurred while loading this job. The job data may be corrupted."
      jobId={jobIdParam}
      jobNumber={jobNumber}
      page="QuoteBuilder"
      componentName="QuoteBuilder"
    >
      <QuoteBuilderContent onJobNumberChange={setJobNumber} />
    </ErrorBoundary>
  );
}

function QuoteBuilderContent({ onJobNumberChange }) {
  const [searchParams] = useSearchParams();
  const routeParams = useParams();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [jobId, setJobId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isCreatingSnapshot, setIsCreatingSnapshot] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [readOnlyVersion, setReadOnlyVersion] = useState(null); // viewing a past version (read-only)
  const [savingVersion, setSavingVersion] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [showSnapshotHistory, setShowSnapshotHistory] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [sendChangeNote, setSendChangeNote] = useState("");
  const [lastSaved, setLastSaved] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const isSendingRef = useRef(false); // true while a send-quote action is in flight (blocks auto-save status regression)
  const jobStatusRef = useRef(null); // latest job.status, read inside the auto-save timer to avoid stale-closure overwrites
  
  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];
  
  const [job, setJob] = useState(() => {
    const s = getInitialJobState(today);
    if (searchParams.get("new") === "retail") {
      s.client_type = "Retail";
      s.margin_percent = 0.45;
    }
    return s;
  });
  jobStatusRef.current = job.status; // keep latest status accessible to async timers

  // Live quote + quantity-break calculation (debounced) — extracted hook
  const { quote, setQuote, isCalculating, quantityBreakResults, isCalculatingQtyBreak } = useQuoteCalculation(job);

  const [showInternal, setShowInternal] = useState(false); // Default OFF
  const [showDesignFeeToClient, setShowDesignFeeToClient] = useState(true); // Default ON - show design fee to client
  const [settings, setSettings] = useState(null);
  const [methodNames, setMethodNames] = useState({});
  const [showClientDetails, setShowClientDetails] = useState(false);
  
  // Colour lookup hook
  const { getColour: lookupColour } = useAllColours();
  
  // Product picker state
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [productPickerTarget, setProductPickerTarget] = useState(null); // garment id to fill
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  
  // Autocomplete state per garment
  const [autocompleteGarmentId, setAutocompleteGarmentId] = useState(null);
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [isSearchingAutocomplete, setIsSearchingAutocomplete] = useState(false);
  const [autocompleteSearchDone, setAutocompleteSearchDone] = useState(false);

  // Quick Add Garment state
  const [quickAddSearch, setQuickAddSearch] = useState("");
  const [quickAddProduct, setQuickAddProduct] = useState(null);
  const [quickAddColour, setQuickAddColour] = useState("");
  const [quickAddQty, setQuickAddQty] = useState("");
  const [quickAddResults, setQuickAddResults] = useState([]);
  const [isSearchingQuickAdd, setIsSearchingQuickAdd] = useState(false);
  const [quickAddSearchDone, setQuickAddSearchDone] = useState(false);

  // Xero integration state
  const [xeroConnected, setXeroConnected] = useState(false);
  const [isCreatingXeroInvoice, setIsCreatingXeroInvoice] = useState(false);
  const [xeroInvoice, setXeroInvoice] = useState(null); // {invoice_id, invoice_number, invoice_url}

  // Gmail integration state
  const [gmailConnected, setGmailConnected] = useState(false);
  const [isEmailingQuote, setIsEmailingQuote] = useState(false);
  const [quoteEmailLog, setQuoteEmailLog] = useState(null); // {emailed_at, emailed_to}

  // Quote sending/approval state
  const [isSendingQuote, setIsSendingQuote] = useState(false);
  const [quoteSentInfo, setQuoteSentInfo] = useState(null); // {sent_at, sent_to, token, public_url}
  const [approvalInfo, setApprovalInfo] = useState(null); // {approved_at, approved_by}
  
  // Job number state
  const [jobNumber, setJobNumberInternal] = useState(null); // AA-0001 format
  
  // Wrapper to update both local and parent state
  const setJobNumber = (num) => {
    setJobNumberInternal(num);
    if (onJobNumberChange) onJobNumberChange(num);
  };

  // Placement template state
  const [selectedTemplate, setSelectedTemplate] = useState("Custom");
  const [showTemplateConfirm, setShowTemplateConfirm] = useState(false);

  // Quote Template state (for saving and applying templates)
  const [showSaveAsTemplateModal, setShowSaveAsTemplateModal] = useState(false);
  const [showApplyTemplateModal, setShowApplyTemplateModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [saveTemplateData, setSaveTemplateData] = useState({ name: "", description: "", category: "General" });
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  // Send Quote Email state
  const [showSendQuoteEmail, setShowSendQuoteEmail] = useState(false);
  const [gmailAccounts, setGmailAccounts] = useState([]);

  // Placement templates configuration
  const PLACEMENT_TEMPLATES = {
    "Custom": [],
    "Left Chest": ["Left Chest"],
    "Front Only": ["Front Print"],
    "Back Only": ["Back Print"],
    "Front + Back": ["Front Print", "Back Print"],
    "Left Chest + Back": ["Left Chest", "Back Print"],
    "Left Chest + Sleeve": ["Left Chest", "Sleeve Print"],
  };

  // Collapsible sections state - Garments & Artwork open by default, others closed
  const [expandedSections, setExpandedSections] = useState({
    jobDetails: false,
    clientDetails: false,
    garments: true,
    artworkGroups: true,
    supplierShipping: false,
    miscCosts: false,
    internalMetrics: false,
  });

  // Quantity Break Mode state
  const [selectedQtyColumn, setSelectedQtyColumn] = useState(0);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Check if job has approved snapshots
  const hasApprovedSnapshot = snapshots.length > 0;
  const latestSnapshot = snapshots.length > 0 ? snapshots[snapshots.length - 1] : null;
  
  // Check if job is locked (approved by client)
  const isApproved = job.status === "Approved";
  const isSent = job.status === "Sent";
  const isChangesRequested = job.status === "Changes Requested";
  const isInProduction = job.status === "In Production";
  
  // Calculate deadline countdown for production jobs
  const deadlineCountdown = (isInProduction || isApproved) && job.deadline 
    ? getDeadlineCountdown(job.deadline) 
    : null;

  // Load settings and job on mount
  useEffect(() => {
    loadSettings();
    loadMethodNames();
    loadXeroStatus();
    loadGmailStatus();
    loadGmailAccounts();
    // Check if loading a saved job — accept ?job={id} or /quote/:jobId
    const loadJobId = searchParams.get("job") || routeParams.jobId;
    if (loadJobId) {
      const versionParam = searchParams.get("version");
      loadJob(loadJobId, versionParam ? parseInt(versionParam, 10) : null);
    }
  }, [searchParams, routeParams.jobId]);

  const loadXeroStatus = async () => {
    try {
      const res = await axios.get(`${API}/xero/status`);
      setXeroConnected(res.data.connected);
    } catch (error) {
      console.error("Failed to load Xero status", error);
    }
  };

  const loadGmailStatus = async () => {
    try {
      const res = await axios.get(`${API}/google/status`);
      setGmailConnected(res.data.connected);
    } catch (error) {
      console.error("Failed to load Gmail status", error);
    }
  };

  // Warn user about unsaved changes before leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };
    
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (error) {
      console.error("Failed to load settings", error);
    }
  };

  const loadMethodNames = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(`${API}/settings/print-method-names`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setMethodNames(res.data || {});
    } catch (error) {
      console.error("Failed to load print method names", error);
    }
  };

  const loadGmailAccounts = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(`${API}/gmail/accounts`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setGmailAccounts(res.data.accounts || []);
      // Check if any account is connected
      const hasConnected = (res.data.accounts || []).some(a => a.connected);
      setGmailConnected(hasConnected);
    } catch (error) {
      console.error("Failed to load Gmail accounts", error);
    }
  };

  // Rehydrate transient product-derived helpers on saved garments (sizes/colours/supplier)
  const enrichGarmentsWithProductData = async (garments) => {
    const ids = [...new Set((garments || []).map((g) => g.product_id).filter((pid) => pid && pid !== "byo-garment-system"))];
    if (ids.length === 0) return;
    try {
      const results = await Promise.all(
        ids.map((pid) => axios.get(`${API}/products/${pid}`).then((r) => r.data).catch(() => null))
      );
      const byId = {};
      results.forEach((p) => { if (p && p.id) byId[p.id] = p; });
      setJob((prev) => ({
        ...prev,
        garments: prev.garments.map((g) => {
          const p = byId[g.product_id];
          if (!p) return g;
          const retail = p.retail_price || p.default_cost;
          return {
            ...g,
            _retailPrice: retail,
            _wholesaleCost: p.default_cost,
            // Keep displayed unit cost aligned with the active mode
            cost: prev.client_type === "Retail" ? retail : p.default_cost,
            _availableColours: p.available_colours || [],
            _availableSizes: p.sizes || [],
            _supplier: p.supplier || "",
          };
        }),
      }));
    } catch (e) {
      // Non-fatal: dropdowns fall back to free text if enrichment fails
    }
  };

  const loadJob = async (id, version = null) => {    try {
      const res = await axios.get(`${API}/jobs/${id}`);
      if (res.data?.job_data) {
        // Normalize job data to prevent render crashes
        let jobData = res.data.job_data;
        const allSnapshots = res.data.snapshots || [];

        // Read-only past-version view: hydrate from that version's frozen full state
        if (version) {
          const snap = allSnapshots.find((s) => s.version === version);
          if (snap && snap.job_data_full) {
            jobData = snap.job_data_full;
            setReadOnlyVersion(version);
          } else {
            toast.error(`Version V${version} can't be opened (no saved detail).`);
            setReadOnlyVersion(null);
          }
        } else {
          setReadOnlyVersion(null);
        }
        const normalizedJob = {
          ...jobData,
          // Ensure arrays exist
          garments: Array.isArray(jobData.garments) ? jobData.garments : [],
          artwork_groups: Array.isArray(jobData.artwork_groups) 
            ? jobData.artwork_groups.map(ag => ({
                ...ag,
                // Ensure required fields have safe defaults
                placement_name: ag.placement_name || ag.name || '',
                allocations: Array.isArray(ag.allocations) ? ag.allocations : [],
                print_method: ag.print_method || '',
              }))
            : [],
          supplier_shipping: Array.isArray(jobData.supplier_shipping) ? jobData.supplier_shipping : [],
          // Ensure client object exists
          client: jobData.client || {
            business_name: "",
            contact_name: "",
            name: "",
            email: "",
            phone: "",
          },
        };
        
        setJob(normalizedJob);
        setJobId(id);
        setSnapshots(res.data.snapshots || []);
        setHasUnsavedChanges(false);
        // Rehydrate product-derived helpers (sizes/colours/supplier) for saved jobs
        enrichGarmentsWithProductData(normalizedJob.garments);
        // Load Xero invoice info if exists
        if (res.data.xero_invoice_id) {
          setXeroInvoice({
            invoice_id: res.data.xero_invoice_id,
            invoice_number: res.data.xero_invoice_number,
            invoice_url: res.data.xero_invoice_url,
          });
        } else {
          setXeroInvoice(null);
        }
        
        // Load email log if exists
        if (res.data.quote_emailed_at) {
          setQuoteEmailLog({
            emailed_at: res.data.quote_emailed_at,
            emailed_to: res.data.quote_emailed_to,
          });
        } else {
          setQuoteEmailLog(null);
        }
        
        // Load quote sent info if exists
        if (res.data.quote_sent_at) {
          const token = res.data.quote_token;
          setQuoteSentInfo({
            sent_at: res.data.quote_sent_at,
            sent_to: res.data.quote_sent_to,
            token: token,
            public_url: token ? `${window.location.origin}/public/quote/${token}` : null,
          });
        } else {
          setQuoteSentInfo(null);
        }
        
        // Load approval info if exists
        if (res.data.approved_at) {
          setApprovalInfo({
            approved_at: res.data.approved_at,
            approved_by: res.data.approved_by,
          });
        } else {
          setApprovalInfo(null);
        }
        
        // Load job number
        setJobNumber(res.data.job_number || null);
        
        toast.success("Job loaded");
      }
    } catch (error) {
      console.error("Failed to load job", error);
      toast.error("Failed to load job");
    }
  };

  // Product picker functions
  const loadProducts = async (search = "") => {
    setIsLoadingProducts(true);
    try {
      const res = await axios.get(`${API}/products`, {
        params: { search, active_only: true, limit: 30 },
      });
      setProducts(res.data.products || []);
    } catch (error) {
      console.error("Failed to load products", error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const openProductPicker = (garmentId) => {
    setProductPickerTarget(garmentId);
    setProductSearch("");
    loadProducts("");
    setShowProductPicker(true);
  };

  const selectProduct = async (product) => {
    if (!productPickerTarget) return;
    
    // Build garment name: "Brand StyleCode ProductName"
    const parts = [product.brand, product.style_code, product.product_name].filter(Boolean);
    const garmentName = parts.join(" ");
    
    // Update the garment with product data including sizes
    setHasUnsavedChanges(true);
    setJob((prev) => ({
      ...prev,
      garments: prev.garments.map((g) =>
        g.id === productPickerTarget
          ? {
              ...g,
              name: garmentName,
              cost: (prev.client_type === "Retail" ? (product.retail_price || product.default_cost) : product.default_cost),
              _retailPrice: product.retail_price || product.default_cost,
              _wholesaleCost: product.default_cost,
              colour: product.available_colours?.[0] || "",
              product_id: product.id,
              _availableColours: product.available_colours || [],
              _availableSizes: product.sizes || [],
              _sizeType: product.size_type || "standard_sizes",
              _productType: product.product_type || "",
              _supplier: product.supplier || "",
              size_breakdown: {}, // Initialize empty size breakdown
            }
          : g
      ),
    }));
    
    // Track product usage
    try {
      await axios.post(`${API}/products/${product.id}/use`);
    } catch (e) {
      // Silently fail - usage tracking is not critical
    }
    
    setShowProductPicker(false);
    setProductPickerTarget(null);
    toast.success(`Selected: ${garmentName}`);
  };

  // Autocomplete search for garment name input
  const searchAutocomplete = async (garmentId, query) => {
    if (!query || query.length < 2) {
      setAutocompleteResults([]);
      setAutocompleteGarmentId(null);
      setAutocompleteSearchDone(false);
      return;
    }
    
    setAutocompleteGarmentId(garmentId);
    setIsSearchingAutocomplete(true);
    setAutocompleteSearchDone(false);
    
    try {
      const res = await axios.get(`${API}/products`, {
        params: { search: query, active_only: true, limit: 6 },
      });
      setAutocompleteResults(res.data.products || []);
    } catch (error) {
      console.error("Autocomplete search failed", error);
      setAutocompleteResults([]);
    } finally {
      setIsSearchingAutocomplete(false);
      setAutocompleteSearchDone(true);
    }
  };

  const selectAutocompleteProduct = async (garmentId, product) => {
    // Build garment name: "Brand StyleCode ProductName"
    const parts = [product.brand, product.style_code, product.product_name].filter(Boolean);
    const garmentName = parts.join(" ");
    
    // Update the garment with product data including sizes
    setHasUnsavedChanges(true);
    setJob((prev) => ({
      ...prev,
      garments: prev.garments.map((g) =>
        g.id === garmentId
          ? {
              ...g,
              name: garmentName,
              cost: (prev.client_type === "Retail" ? (product.retail_price || product.default_cost) : product.default_cost),
              _retailPrice: product.retail_price || product.default_cost,
              _wholesaleCost: product.default_cost,
              colour: product.available_colours?.[0] || "",
              product_id: product.id,
              _availableColours: product.available_colours || [],
              _availableSizes: product.sizes || [],
              _sizeType: product.size_type || "standard_sizes",
              _productType: product.product_type || "",
              _supplier: product.supplier || "",
              size_breakdown: {}, // Initialize empty size breakdown
            }
          : g
      ),
    }));
    
    // Track product usage
    try {
      await axios.post(`${API}/products/${product.id}/use`);
    } catch (e) {
      // Silently fail - usage tracking is not critical
    }
    
    // Close autocomplete
    setAutocompleteResults([]);
    setAutocompleteGarmentId(null);
  };

  const closeAutocomplete = () => {
    // Delay to allow click on suggestion to register
    setTimeout(() => {
      setAutocompleteResults([]);
      setAutocompleteGarmentId(null);
    }, 150);
  };

  // Quick Add Garment functions
  const searchQuickAdd = async (query) => {
    if (!query || query.length < 2) {
      setQuickAddResults([]);
      setQuickAddSearchDone(false);
      return;
    }
    
    setIsSearchingQuickAdd(true);
    setQuickAddSearchDone(false);
    try {
      const res = await axios.get(`${API}/products`, {
        params: { search: query, active_only: true, limit: 8 },
      });
      setQuickAddResults(res.data.products || []);
    } catch (error) {
      console.error("Quick add search failed", error);
      setQuickAddResults([]);
    } finally {
      setIsSearchingQuickAdd(false);
      setQuickAddSearchDone(true);
    }
  };

  const selectQuickAddProduct = (product) => {
    setQuickAddProduct(product);
    setQuickAddSearch(`${product.brand || ""} ${product.style_code || ""} ${product.product_name || ""}`.trim());
    setQuickAddResults([]);
    // Set first colour if available
    if (product.available_colours?.length > 0) {
      setQuickAddColour(product.available_colours[0]);
    } else {
      setQuickAddColour("");
    }
  };

  const addQuickGarment = async () => {
    if (!quickAddProduct) {
      toast.error("Please select a product");
      return;
    }
    
    const qty = parseInt(quickAddQty, 10);
    if (!qty || qty <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }
    
    // Create garment name from product
    const parts = [quickAddProduct.brand, quickAddProduct.style_code, quickAddProduct.product_name].filter(Boolean);
    const garmentName = parts.join(" ");
    
    const newGarment = {
      id: crypto.randomUUID(),
      name: garmentName,
      quantity: qty,
      cost: (job.client_type === "Retail"
        ? (quickAddProduct.retail_price || quickAddProduct.default_cost)
        : quickAddProduct.default_cost) || 0,
      _retailPrice: quickAddProduct.retail_price || quickAddProduct.default_cost,
      _wholesaleCost: quickAddProduct.default_cost,
      colour: quickAddColour || "",
      product_id: quickAddProduct.id,
      _availableColours: quickAddProduct.available_colours || [],
      _availableSizes: quickAddProduct.sizes || [],
      _supplier: quickAddProduct.supplier || "",
    };
    
    setHasUnsavedChanges(true);
    setJob((prev) => ({
      ...prev,
      garments: [...prev.garments, newGarment],
    }));
    
    // Track product usage
    try {
      await axios.post(`${API}/products/${quickAddProduct.id}/use`);
    } catch (e) {
      // Silently fail - usage tracking is non-critical analytics
    }
    
    // Clear quick add form
    setQuickAddSearch("");
    setQuickAddProduct(null);
    setQuickAddColour("");
    setQuickAddQty("");
    setQuickAddResults([]);
    
    toast.success(`Added: ${garmentName}`);
  };

  const saveJob = async () => {
    if (readOnlyVersion) {
      toast.error("You're viewing a past version (read-only). Revert it to make edits.");
      return;
    }
    setIsSaving(true);
    try {
      if (jobId) {
        // Update existing job
        await axios.put(`${API}/jobs/${jobId}`, job);
        setHasUnsavedChanges(false);
        setLastSaved(new Date());
        toast.success("Job saved");
      } else {
        // Save new job
        const res = await axios.post(`${API}/jobs/save`, job);
        setJobId(res.data.id);
        setJobNumber(res.data.job_number); // Capture job number from response
        // Server may set a different starting status (Retail sales start In Production)
        if (res.data.status && res.data.status !== job.status) {
          setJob((prev) => ({ ...prev, status: res.data.status }));
        }
        // Reflect the new job id in the URL (without remounting) so a refresh keeps context
        window.history.replaceState(null, "", `/quote?job=${res.data.id}`);
        setHasUnsavedChanges(false);
        setLastSaved(new Date());
        toast.success("Job saved");
      }
    } catch (error) {
      console.error("Failed to save job", error);
      toast.error("Failed to save job");
    } finally {
      setIsSaving(false);
    }
  };

  // Convert an Initial Contact enquiry from a Job into a non-print Task.
  // Retains client details and notes; the garment/print/shipping sections are hidden
  // for tasks. The card stays in the Initial Contact column (status unchanged).
  const convertToTask = async () => {
    if (readOnlyVersion) {
      toast.error("You're viewing a past version (read-only). Revert it to make edits.");
      return;
    }
    if (!jobId) {
      toast.error("Save the enquiry first, then convert to a task");
      return;
    }
    const updated = { ...job, job_type: "task" };
    setJob(updated);
    try {
      await axios.put(`${API}/jobs/${jobId}`, updated);
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      toast.success("Converted to Task");
    } catch (error) {
      console.error("Failed to convert to task", error);
      toast.error("Failed to convert to task");
    }
  };

  // Auto-save effect (debounced) - only auto-saves existing jobs in Draft status
  useEffect(() => {
    // Clear any existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Only auto-save if:
    // 1. Job has an ID (already saved once)
    // 2. Has unsaved changes
    // 3. Job is in Draft status
    // 4. Not currently saving
    if (jobId && hasUnsavedChanges && job.status === "Draft" && !isSaving && !readOnlyVersion) {
      autoSaveTimerRef.current = setTimeout(async () => {
        // Guard against overwriting a status that changed since this timer was scheduled
        // (e.g. a quote was just sent → status is now "Sent"). Prevents the auto-save
        // from regressing the job back to "Draft".
        if (isSendingRef.current || jobStatusRef.current !== "Draft") {
          return;
        }
        try {
          await axios.put(`${API}/jobs/${jobId}`, { ...job, status: "Draft" });
          setHasUnsavedChanges(false);
          setLastSaved(new Date());
          // Subtle notification - don't use toast for auto-save to avoid spam
          logger.log("Auto-saved draft at", new Date().toLocaleTimeString());
        } catch (error) {
          console.error("Auto-save failed", error);
        }
      }, 5000); // 5 second debounce
    }
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [job, jobId, hasUnsavedChanges, isSaving]);

  // ==================== TEMPLATE FUNCTIONS ====================
  
  // Load templates for the apply template modal
  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await axios.get(`${API}/templates`);
      setTemplates(res.data.templates || []);
    } catch (error) {
      console.error("Failed to load templates", error);
      toast.error("Failed to load templates");
    } finally {
      setTemplatesLoading(false);
    }
  };
  
  // Open Save as Template modal
  const openSaveAsTemplate = () => {
    if (!jobId) {
      toast.error("Save the job first");
      return;
    }
    setSaveTemplateData({
      name: job.job_name ? `${job.job_name} Template` : "New Template",
      description: "",
      category: "General",
    });
    setShowSaveAsTemplateModal(true);
  };
  
  // Save current job as template
  const saveAsTemplate = async () => {
    if (!saveTemplateData.name.trim()) {
      toast.error("Template name is required");
      return;
    }
    
    setIsSavingTemplate(true);
    try {
      await axios.post(
        `${API}/templates/from-job/${jobId}`,
        null,
        { params: saveTemplateData }
      );
      toast.success("Template created successfully");
      setShowSaveAsTemplateModal(false);
    } catch (error) {
      console.error("Failed to create template", error);
      toast.error(error.response?.data?.detail || "Failed to create template");
    } finally {
      setIsSavingTemplate(false);
    }
  };
  
  // Open Apply Template modal
  const openApplyTemplate = () => {
    loadTemplates();
    setShowApplyTemplateModal(true);
  };
  
  // Apply a template to the current job
  const applyTemplate = async (template) => {
    // Confirm if job already has garments or artwork
    if (job.garments.length > 0 || job.artwork_groups.length > 0) {
      if (!window.confirm("This will replace existing garments and print groups. Continue?")) {
        return;
      }
    }
    
    try {
      // Increment template use count
      await axios.post(`${API}/templates/${template.id}/use`);
      
      // Convert template garments to job garments (generate new IDs)
      const newGarments = (template.garments || []).map(g => ({
        id: generateId(),
        name: g.name || "",
        colour: g.colour || "",
        cost: g.cost || 0,
        quantity: g.quantity || 1,
        product_id: g.product_id || null,
      }));
      
      // Build garment ID mapping (template index -> new garment ID)
      const garmentIdMap = {};
      newGarments.forEach((g, idx) => {
        garmentIdMap[idx] = g.id;
      });
      
      // Convert template artwork groups to job artwork groups
      const newArtworkGroups = (template.artwork_groups || []).map(ag => ({
        id: generateId(),
        print_method: ag.print_method || "",
        placement_name: ag.placement_name || "",
        size_tier: ag.size_tier || "",
        allocations: (ag.allocations || []).map(alloc => ({
          garment_id: garmentIdMap[alloc.garment_index] || "",
          placement_count: alloc.placement_count || 1,
        })).filter(a => a.garment_id), // Only include allocations with valid garment IDs
        blocker: ag.blocker,
        colours: ag.colours,
        print_colour: ag.print_colour,
        light_on_dark: ag.light_on_dark,
        prints_per_sheet: ag.prints_per_sheet,
        custom_colour_required: ag.custom_colour_required,
        stitch_count: ag.stitch_count,
        override_sell_ex_gst: ag.override_sell_ex_gst,
        override_cost_ex_gst: ag.override_cost_ex_gst,
        override_label: ag.override_label,
        override_time_mins: ag.override_time_mins,
        override_notes: ag.override_notes,
      }));
      
      // Apply template data to job (preserve customer info)
      setJob(prev => ({
        ...prev,
        garments: newGarments,
        artwork_groups: newArtworkGroups,
        // Apply default margin if specified and different from current
        ...(template.default_margin_percent && {
          margin_percent: template.default_margin_percent,
        }),
        // Apply default client type if specified
        ...(template.default_client_type && {
          client_type: template.default_client_type,
        }),
        // Append template notes if present
        ...(template.notes && {
          notes: prev.notes ? `${prev.notes}\n\n${template.notes}` : template.notes,
        }),
      }));
      
      setHasUnsavedChanges(true);
      setShowApplyTemplateModal(false);
      toast.success(`Template "${template.name}" applied`);
      
    } catch (error) {
      console.error("Failed to apply template", error);
      toast.error("Failed to apply template");
    }
  };

  // Mark job as Sent (manual - without sending email)
  const markAsSent = async () => {
    if (!jobId) {
      toast.error("Save the job first");
      return;
    }
    
    setIsSendingQuote(true);
    try {
      // Update job status to Sent
      const updatedJob = { ...job, status: "Sent" };
      await axios.put(`${API}/jobs/${jobId}`, updatedJob);
      setJob(updatedJob);
      setHasUnsavedChanges(false);
      toast.success("Quote marked as sent");
    } catch (error) {
      console.error("Failed to mark quote as sent", error);
      toast.error("Failed to update status");
    } finally {
      setIsSendingQuote(false);
    }
  };

  const createSnapshot = async (isRevision = false) => {
    if (!jobId) {
      toast.error("Save the job first before creating a snapshot");
      return;
    }
    
    // Save job first to ensure latest data
    setIsSaving(true);
    try {
      await axios.put(`${API}/jobs/${jobId}`, job);
    } catch (error) {
      console.error("Failed to save job before snapshot", error);
      toast.error("Failed to save job");
      setIsSaving(false);
      return;
    }
    setIsSaving(false);
    
    setIsCreatingSnapshot(true);
    try {
      const res = await axios.post(`${API}/jobs/${jobId}/snapshot`, {
        design_shown_to_client: showDesignFeeToClient,
      });
      
      // Update local state
      setSnapshots((prev) => [...prev, res.data.snapshot]);
      setJob((prev) => ({ ...prev, status: "Approved" }));
      setHasUnsavedChanges(false);
      
      toast.success(isRevision 
        ? `Revised snapshot v${res.data.version} created` 
        : `Approved! Snapshot v${res.data.version} created`
      );
    } catch (error) {
      console.error("Failed to create snapshot", error);
      toast.error("Failed to create snapshot");
    } finally {
      setIsCreatingSnapshot(false);
    }
  };

  const duplicateJob = async () => {
    if (!jobId) {
      toast.error("Save the job first before duplicating");
      return;
    }
    setIsDuplicating(true);
    try {
      const res = await axios.post(`${API}/jobs/${jobId}/duplicate`);
      toast.success("Job duplicated successfully");
      navigate(`/quote?job=${res.data.id}`);
    } catch (error) {
      console.error("Failed to duplicate job", error);
      toast.error("Failed to duplicate job");
    } finally {
      setIsDuplicating(false);
    }
  };

  const startNewJob = async () => {
    // Auto-save the current job as a Draft if it has content (so user doesn't lose work)
    const hasContent =
      job?.job_name?.trim() ||
      job?.client?.business_name?.trim() ||
      job?.client?.contact_name?.trim() ||
      job?.client?.email?.trim() ||
      (job?.garments?.length > 0) ||
      (job?.artwork_groups?.length > 0);

    if (hasContent) {
      try {
        if (jobId) {
          // Existing job with unsaved changes - persist current state
          if (hasUnsavedChanges) {
            await axios.put(`${API}/jobs/${jobId}`, job);
            toast.success("Current quote saved");
          }
        } else {
          // New unsaved job - create as Draft so it lands on the Jobs board
          const draft = { ...job, status: job?.status || "Draft" };
          await axios.post(`${API}/jobs`, draft);
          toast.success("Current quote saved as Draft");
        }
      } catch (error) {
        console.error("Failed to auto-save current quote before new", error);
        toast.error("Could not save current quote - aborting new quote");
        return;
      }
    }

    // Get next job number
    let nextJobNumber = null;
    try {
      const res = await axios.get(`${API}/jobs/next-job-number`);
      nextJobNumber = res.data.job_number;
    } catch (error) {
      console.error("Failed to get next job number", error);
    }
    
    // Reset all state
    setJobId(null);
    setJobNumber(nextJobNumber);
    setSnapshots([]);
    setSelectedSnapshot(null);
    setQuote(null);
    setHasUnsavedChanges(false);
    setJob({
      job_name: "",
      quote_date: new Date().toISOString().split('T')[0],
      deadline: "",
      due_date: "",
      due_date_mode: "date",
      due_date_value: null,
      status: "Draft",
      customer_id: null,
      client: {
        business_name: "",
        contact_name: "",
        name: "",
        email: "",
        phone: "",
        website: "",
        physical_address: "",
        postal_address: "",
      },
      client_type: "Business",
      margin_percent: 0.35,
      design_required: false,
      design_fee: 0,
      apply_margin_to_design: false,
      garments: [],
      artwork_groups: [],
      supplier_shipping: [],
      shipping: 0,
      misc_costs: 0,
    });
    
    // Stay on the Quote Builder route with no jobId
    navigate("/quote", { replace: true });
    toast.success("New job started");
  };

  const downloadPDF = () => {
    try {
      // Use snapshot if available, otherwise use live quote
      const fileName = generateClientQuotePDF({
        jobData: job,
        snapshot: latestSnapshot,
        liveQuote: quote,
        showDesignFeeToClient: showDesignFeeToClient,
        settings: settings,
        jobNumber: jobNumber,
        version: latestSnapshot?.version,
        methodNames: methodNames,
      });
      
      if (fileName) {
        toast.success(`Downloaded: ${fileName}`);
      } else {
        toast.error("Failed to generate PDF");
      }
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error(`PDF generation failed: ${error.message || "Unknown error"}`);
    }
  };

  // Download Quantity Break Comparison PDF (server-side)
  const downloadQuantityBreakPDF = async () => {
    if (!jobId) {
      toast.error("Save the job first to download the comparison PDF");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/jobs/${jobId}/download-quantity-break-pdf`,
        {},
        { responseType: 'blob' }
      );
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `QuantityBreakQuote_${jobNumber || 'DRAFT'}_${job.job_name || 'Quote'}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded: ${filename}`);
    } catch (error) {
      console.error("Quantity Break PDF download error:", error);
      toast.error(error.response?.data?.detail || "Failed to download Comparison PDF");
    }
  };

  // Download Individual Quote PDF for the selected quantity column.
  // Generated client-side with generateClientQuotePDF so it matches the standard quote format EXACTLY.
  const downloadIndividualQuotePDF = async () => {
    const settingsArr = job.quantity_break_settings || [];
    const col = settingsArr[selectedQtyColumn];
    if (!col || !col.quantity) {
      toast.error("Please select a quantity column");
      return;
    }
    const selectedQty = col.quantity;

    try {
      // Build a standard (non-break) job for the selected quantity, mirroring the
      // server's individual-quote logic (each garment gets the selected quantity,
      // column margin/shipping applied, supplier shipping filtered for this break).
      const modifiedGarments = (job.garments || []).map((g) => ({ ...g, quantity: selectedQty }));
      const filteredSupplierShipping = (job.supplier_shipping || []).filter(
        (ss) =>
          !ss.allocated_qty_breaks ||
          ss.allocated_qty_breaks.length === 0 ||
          ss.allocated_qty_breaks.includes(selectedQty)
      );
      const modifiedJob = {
        ...job,
        is_quantity_break: false,
        margin_percent: col.margin_percent ?? job.margin_percent,
        shipping: col.shipping ?? 0,
        garments: modifiedGarments,
        supplier_shipping: filteredSupplierShipping,
      };

      const res = await axios.post(`${API}/quote/calculate`, modifiedJob);

      const fileName = generateClientQuotePDF({
        jobData: modifiedJob,
        liveQuote: res.data,
        showDesignFeeToClient: showDesignFeeToClient,
        settings: settings,
        jobNumber: jobNumber,
        fileNameSuffix: `_${selectedQty}units`,
        methodNames: methodNames,
      });

      if (fileName) {
        toast.success(`Downloaded: ${fileName}`);
      } else {
        toast.error("Failed to generate PDF");
      }
    } catch (error) {
      console.error("Individual Quote PDF download error:", error);
      toast.error(error.response?.data?.detail || "Failed to download Quote PDF");
    }
  };

  const downloadProductionSheet = async () => {
    if (job.status === "Draft") {
      toast.error("Cannot generate production sheet for Draft jobs");
      return;
    }
    
    if (!jobId) {
      toast.error("Save the job first");
      return;
    }
    
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/production-sheet`, {
        responseType: 'blob',
      });
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `ProductionSheet_${jobNumber || 'DRAFT'}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded: ${filename}`);
    } catch (error) {
      console.error("Failed to download production sheet:", error);
      const errorMsg = error.response?.data?.detail || "Failed to generate production sheet";
      toast.error(errorMsg);
    }
  };

  const createXeroInvoice = async (confirmDuplicate = false) => {
    // Defensive: ensure confirmDuplicate is a boolean (callers sometimes pass events)
    const confirmDup = confirmDuplicate === true;
    if (!jobId) {
      toast.error("Save the job first");
      return;
    }
    
    if (job.status !== "Approved") {
      toast.error("Job must be Approved to create a Xero invoice");
      return;
    }
    
    setIsCreatingXeroInvoice(true);
    try {
      const res = await axios.post(`${API}/jobs/${jobId}/create-xero-invoice`, {
        confirm_duplicate: confirmDup,
      });
      
      setXeroInvoice({
        invoice_id: res.data.invoice_id,
        invoice_number: res.data.invoice_number,
        invoice_url: res.data.invoice_url,
      });
      
      toast.success(res.data.message);
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || "Failed to create Xero invoice";
      
      // Handle duplicate invoice case
      if (error.response?.status === 409) {
        if (window.confirm("An invoice already exists for this job. Create another draft invoice?")) {
          createXeroInvoice(true);
        }
      } else {
        // Show full error message with long duration so user can read it
        toast.error(detail, { duration: 12000 });
      }
      console.error("Xero invoice creation failed", error.response?.status, error.response?.data, error);
    } finally {
      setIsCreatingXeroInvoice(false);
    }
  };

  const emailQuote = async (confirmResend = false) => {
    if (!jobId) {
      toast.error("Save the job first");
      return;
    }
    if (job.status !== "Approved") {
      toast.error("Job must be Approved to email quote");
      return;
    }
    if (!latestSnapshot?.client_email) {
      toast.error("Client email is required to send quote");
      return;
    }
    if (!gmailConnected) {
      toast.error("Gmail not connected. Please connect Gmail in Settings.");
      return;
    }
    if (quoteEmailLog && !confirmResend) {
      if (!window.confirm(`Quote was already emailed on ${new Date(quoteEmailLog.emailed_at).toLocaleString()} to ${quoteEmailLog.emailed_to}. Send again?`)) {
        return;
      }
    }
    setIsEmailingQuote(true);
    try {
      const res = await axios.post(`${API}/jobs/${jobId}/email-quote`);
      setQuoteEmailLog({
        emailed_at: res.data.emailed_at,
        emailed_to: res.data.emailed_to,
      });
      toast.success(res.data.message);
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to email quote";
      toast.error(detail);
      console.error("Email quote failed", error);
    } finally {
      setIsEmailingQuote(false);
    }
  };

  const sendQuoteToClient = async () => {
    if (!jobId) {
      toast.error("Save the job first");
      return;
    }
    if (!job.client?.email) {
      toast.error("Client email is required to send quote");
      return;
    }
    setIsSendingQuote(true);
    isSendingRef.current = true;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    try {
      const res = await axios.post(`${API}/jobs/${jobId}/send-quote`);
      setQuoteSentInfo({
        sent_at: new Date().toISOString(),
        sent_to: res.data.sent_to,
        token: res.data.token,
        public_url: res.data.public_url,
      });
      setJob({ ...job, status: "Sent" });
      jobStatusRef.current = "Sent";
      setHasUnsavedChanges(false);
      setSnapshots([...snapshots, { version: res.data.snapshot_version, status_at_snapshot: "Sent" }]);
      toast.success(res.data.message);
      refreshSnapshots();
      if (res.data.public_url) {
        navigator.clipboard.writeText(res.data.public_url);
        toast.info("Quote link copied to clipboard!");
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to send quote";
      toast.error(detail);
      console.error("Send quote failed", error);
    } finally {
      setIsSendingQuote(false);
      isSendingRef.current = false;
    }
  };

  // Send Quote via Email with PDF attachment
  const sendQuoteViaEmail = async (emailData) => {
    if (!jobId) {
      toast.error("Save the job first");
      return false;
    }
    
    isSendingRef.current = true;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.post(`${API}/jobs/${jobId}/send-quote-email`, {
        to: emailData.to,
        subject: emailData.subject,
        body_html: emailData.body.replace(/\n/g, "<br>"),
        from_account: emailData.from,
        change_note: sendChangeNote || "",
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      // Update job status to Sent
      setJob({ ...job, status: "Sent" });
      jobStatusRef.current = "Sent";
      setHasUnsavedChanges(false);
      setSnapshots([...snapshots, { version: res.data.snapshot_version || snapshots.length + 1, status_at_snapshot: "Sent" }]);
      
      // Update quote sent info
      setQuoteSentInfo({
        sent_at: res.data.sent_at || new Date().toISOString(),
        sent_to: emailData.to,
        via_email: true,
      });
      
      toast.success("Quote sent via email!");
      refreshSnapshots();
      setSendChangeNote("");
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to send quote email";
      toast.error(detail);
      console.error("Send quote email failed", error);
      return false;
    } finally {
      isSendingRef.current = false;
    }
  };

  // ==================== QUOTE VERSIONING (#4) ====================
  const refreshSnapshots = async () => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${API}/jobs/${jobId}`);
      setSnapshots(res.data.snapshots || []);
    } catch (e) {
      console.error("Failed to refresh versions", e);
    }
  };

  const handleSaveVersion = async () => {
    if (!jobId) {
      toast.error("Save the job first");
      return;
    }
    const note = window.prompt("Add a note for this version (optional):", "") ?? "";
    setSavingVersion(true);
    try {
      await axios.put(`${API}/jobs/${jobId}`, job);
      const res = await axios.post(`${API}/jobs/${jobId}/save-version`, { change_note: note });
      await refreshSnapshots();
      setHasUnsavedChanges(false);
      toast.success(`Saved as V${res.data.version} — ready to send to your customer.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save version");
    } finally {
      setSavingVersion(false);
    }
  };

  // Version that a send will actually use (mirrors backend _resolve_send_version)
  const getSendableVersion = () => {
    const latest = snapshots.length ? snapshots[snapshots.length - 1] : null;
    if (latest && latest.is_manual_version) {
      return latest.version;
    }
    return snapshots.length + 1;
  };

  // Guarded entry point for sending — blocks unsaved/read-only, then confirms
  const handleSendQuoteClick = () => {
    if (readOnlyVersion) {
      toast.error(`You're viewing V${readOnlyVersion} (read-only). Return to the current version before sending.`);
      return;
    }
    if (hasUnsavedChanges) {
      toast.error('Unsaved changes — click "Save current as version" first so this version is locked before you send it.');
      return;
    }
    if (!job.client?.email) {
      toast.error("Client email is required to send quote");
      return;
    }
    setShowSendConfirm(true);
  };

  const handleRevertVersion = async (version) => {
    if (!jobId) return;
    if (!window.confirm(`Revert to V${version}? This restores that version's details as an editable draft. Your version history is kept.`)) return;
    try {
      await axios.post(`${API}/jobs/${jobId}/revert/${version}`);
      toast.success(`Reverted to V${version}`);
      setReadOnlyVersion(null);
      navigate(`/quote?job=${jobId}`);
      await loadJob(jobId, null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revert");
    }
  };

  const openVersionInNewTab = (version) => {
    if (!jobId) return;
    window.open(`/quote?job=${jobId}&version=${version}`, "_blank");
  };

  const exitReadOnly = () => {
    setReadOnlyVersion(null);
    navigate(`/quote?job=${jobId}`);
    loadJob(jobId, null);
  };

  const updateClient = (field, value) => {
    setHasUnsavedChanges(true);
    setJob({
      ...job,
      client: { ...job.client, [field]: value },
    });
  };

  // Auto-calculate when job changes → handled by useQuoteCalculation hook

  // Initialize quantity break settings when mode is enabled
  const enableQuantityBreakMode = (enabled) => {
    setHasUnsavedChanges(true);
    if (enabled && (!job.quantity_break_settings || job.quantity_break_settings.length === 0)) {
      // Initialize with default columns: 25, 50, 100
      setJob({
        ...job,
        is_quantity_break: true,
        quantity_break_settings: [
          { quantity: 25, margin_percent: 0.35, shipping: 0 },
          { quantity: 50, margin_percent: 0.35, shipping: 0 },
          { quantity: 100, margin_percent: 0.35, shipping: 0 },
        ],
      });
    } else {
      setJob({ ...job, is_quantity_break: enabled });
    }
  };

  // Helper to update job and mark as having unsaved changes
  const updateJob = (updates) => {
    setHasUnsavedChanges(true);
    setJob((prev) => ({ ...prev, ...updates }));
  };

  // Client type change handler (Business/Retail toggle in the job header).
  // Re-prices product-backed garments to the correct source: retail_price for
  // Retail, wholesale (default_cost) for Business. Manual/BYO garments untouched.
  const handleClientTypeChange = (value) => {
    setHasUnsavedChanges(true);
    const toRetail = value === "Retail";
    setJob((prev) => ({
      ...prev,
      client_type: value,
      margin_percent: CLIENT_TYPES[value] ?? prev.margin_percent,
      garments: (prev.garments || []).map((g) => {
        if (g.is_byo || g.product_id === "byo-garment-system" || !g.product_id) return g;
        if (toRetail && g._retailPrice != null) return { ...g, cost: g._retailPrice };
        if (!toRetail && g._wholesaleCost != null) return { ...g, cost: g._wholesaleCost };
        return g;
      }),
    }));
  };

  // Garment handlers
  const addGarment = () => {
    const newGarmentId = crypto.randomUUID();
    const newGarment = { id: newGarmentId, name: "", colour: "", cost: 0, quantity: 1 };
    
    // Also add allocation entries in all existing artwork groups
    const updatedArtworkGroups = job.artwork_groups.map((a) => ({
      ...a,
      allocations: [...(a.allocations || []), { garment_id: newGarmentId, placement_count: 0 }],
    }));
    
    setHasUnsavedChanges(true);
    setJob({
      ...job,
      garments: [...job.garments, newGarment],
      artwork_groups: updatedArtworkGroups,
    });
  };

  const updateGarment = (id, field, value) => {
    setHasUnsavedChanges(true);
    setJob({
      ...job,
      garments: job.garments.map((g) =>
        g.id === id ? { ...g, [field]: value } : g
      ),
    });
  };

  // Batched multi-field garment update (functional setState = safe for multiple fields at once)
  const updateGarmentFields = (id, fields) => {
    setHasUnsavedChanges(true);
    setJob((prev) => ({
      ...prev,
      garments: prev.garments.map((g) =>
        g.id === id ? { ...g, ...fields } : g
      ),
    }));
  };

  // Atomic update for garment pricing overrides (margin_override + price_adjustment)
  // Single state update to avoid race conditions and ensure immediate quote recalculation
  const updateGarmentOverrides = (id, overrides) => {
    setHasUnsavedChanges(true);
    setJob({
      ...job,
      garments: job.garments.map((g) =>
        g.id === id ? { ...g, ...overrides } : g
      ),
    });
  };

  const removeGarment = (id) => {
    setHasUnsavedChanges(true);
    // Also remove allocation entries from all artwork groups
    const updatedArtworkGroups = job.artwork_groups.map((a) => ({
      ...a,
      allocations: (a.allocations || []).filter((al) => al.garment_id !== id),
    }));
    
    setJob({
      ...job,
      garments: job.garments.filter((g) => g.id !== id),
      artwork_groups: updatedArtworkGroups,
    });
  };

  // Duplicate a garment (keeps all fields including price overrides)
  const duplicateGarment = (id) => {
    const garmentToDuplicate = job.garments.find((g) => g.id === id);
    if (!garmentToDuplicate) return;
    
    const newGarmentId = crypto.randomUUID();
    const newGarment = {
      ...garmentToDuplicate,
      id: newGarmentId,
      // Retain margin_override and price_adjustment (true copy)
    };
    
    // Also copy allocation entries in all existing artwork groups
    const updatedArtworkGroups = job.artwork_groups.map((a) => {
      const originalAlloc = (a.allocations || []).find((al) => al.garment_id === id);
      return {
        ...a,
        allocations: [
          ...(a.allocations || []),
          { 
            garment_id: newGarmentId, 
            placement_count: originalAlloc?.placement_count || 0 
          },
        ],
      };
    });
    
    setHasUnsavedChanges(true);
    setJob({
      ...job,
      garments: [...job.garments, newGarment],
      artwork_groups: updatedArtworkGroups,
    });
    
    toast.success("Garment duplicated");
  };

  // Artwork group handlers
  const addArtworkGroup = () => {
    // Create allocations for all existing garments with default 0
    const initialAllocations = job.garments.map((g) => ({
      garment_id: g.id,
      placement_count: 0,
    }));
    
    setHasUnsavedChanges(true);
    setJob({
      ...job,
      artwork_groups: [
        ...job.artwork_groups,
        {
          id: crypto.randomUUID(),
          print_method: "UltraColour Max",
          placement_name: "",
          size_tier: "M (Up to 315cm² - Standard Chest)",
          allocations: initialAllocations,
          compare_enabled: false,
          blocker: "Standard",
          colours: 1,
          print_colour: "Other",
          light_on_dark: false,
          prints_per_sheet: 1,
          custom_colour_required: false,
          stitch_count: 9999,
          // Manual Override fields (all optional)
          override_sell_ex_gst: null,
          override_cost_ex_gst: null,
          override_label: "",
          override_time_mins: null,
          manual_cost: 0, // Legacy field kept for backward compatibility
        },
      ],
    });
  };

  const updateArtworkGroup = (id, field, value) => {
    // Special handling for stitch_count - enforce minimum of 9999
    // (Allow any value >= 9999; do not auto-round so users can freely increment/decrement)
    if (field === "stitch_count") {
      const minStitches = 9999;
      let newValue = parseInt(value);
      if (isNaN(newValue) || newValue < minStitches) {
        newValue = minStitches;
      }
      value = newValue;
    }
    
    setHasUnsavedChanges(true);

    const DEFAULT_CMP_INPUTS = {
      garments_per_sheet: 1, colour_count: 1, black_or_white_only: false,
      light_on_dark: false, custom_colour_count: 0, cuts_per_sheet: 0, sheet_size: "Standard",
    };

    // Unified comparison: when a target method is chosen, seed the shared set-level inputs
    // (used only when the chosen target is a sheet-based method).
    if (field === "comparison_target" && value) {
      const patch = {
        artwork_groups: job.artwork_groups.map((a) => (a.id === id ? { ...a, [field]: value } : a)),
      };
      if (!job.comparison_inputs) {
        patch.comparison_inputs = { ...DEFAULT_CMP_INPUTS };
      }
      // Reset the virtual target-method config so the panel collects that method's own inputs.
      patch.comparison_target_config = {
        print_method: value, screens: 1, prints_per_sheet: 1, sheet_size: "Standard",
        print_colour: "Other", light_on_dark: false, custom_colour_count: 0, cuts_per_sheet: 0,
        tsp_colours: 1, tsp_selected_charges: ["Film", "Screen"], stitch_count: 9999,
      };
      setJob({ ...job, ...patch });
      return;
    }

    setJob({
      ...job,
      artwork_groups: job.artwork_groups.map((a) =>
        a.id === id ? { ...a, [field]: value } : a
      ),
    });
  };

  // Batched multi-field update for a print group (functional setState = safe for multiple fields at once)
  const updateArtworkGroupFields = (id, fields) => {
    setHasUnsavedChanges(true);
    setJob((prev) => ({
      ...prev,
      artwork_groups: prev.artwork_groups.map((a) =>
        a.id === id ? { ...a, ...fields } : a
      ),
    }));
  };

  const updateArtworkAllocation = (artworkId, garmentId, placementCount) => {
    setHasUnsavedChanges(true);
    setJob({
      ...job,
      artwork_groups: job.artwork_groups.map((a) => {
        if (a.id !== artworkId) return a;
        
        const allocations = a.allocations || [];
        const existingAlloc = allocations.find((al) => al.garment_id === garmentId);
        if (existingAlloc) {
          return {
            ...a,
            allocations: allocations.map((al) =>
              al.garment_id === garmentId ? { ...al, placement_count: placementCount } : al
            ),
          };
        } else {
          return {
            ...a,
            allocations: [...allocations, { garment_id: garmentId, placement_count: placementCount }],
          };
        }
      }),
    });
  };

  const removeArtworkGroup = (id) => {
    setHasUnsavedChanges(true);
    setJob({
      ...job,
      artwork_groups: job.artwork_groups.filter((a) => a.id !== id),
    });
  };

  // Placement Template handlers
  const applyPlacementTemplate = (mode = "add") => {
    const placements = PLACEMENT_TEMPLATES[selectedTemplate];
    if (!placements || placements.length === 0) {
      toast.info("Select a template first");
      return;
    }

    // Check for duplicates
    const existingPlacements = job.artwork_groups.map(a => (a.placement_name || '').toLowerCase());
    const duplicates = placements.filter(p => existingPlacements.includes(p.toLowerCase()));
    
    if (duplicates.length > 0 && mode === "add") {
      toast.warning(`Some placements already exist: ${duplicates.join(", ")}`);
    }

    // Create allocations for all existing garments with default 1 placement
    const createAllocations = () => job.garments.map((g) => ({
      garment_id: g.id,
      placement_count: 1,
    }));

    // Create new artwork groups from template
    const newGroups = placements
      .filter(placement => mode === "replace" || !existingPlacements.includes(placement.toLowerCase()))
      .map(placement => ({
        id: crypto.randomUUID(),
        print_method: "", // Blank - user chooses
        placement_name: placement,
        size_tier: "", // Blank - user chooses
        allocations: createAllocations(),
        compare_enabled: false,
        blocker: "Standard",
        colours: 1,
        print_colour: "Other",
        light_on_dark: false,
        prints_per_sheet: 1,
        custom_colour_required: false,
        stitch_count: 9999,
        // Manual Override fields (all optional)
        override_sell_ex_gst: null,
        override_cost_ex_gst: null,
        override_label: "",
        override_time_mins: null,
        manual_cost: 0,
      }));

    if (newGroups.length === 0) {
      toast.info("All template placements already exist");
      return;
    }

    setHasUnsavedChanges(true);
    if (mode === "replace") {
      setJob({ ...job, artwork_groups: newGroups });
      toast.success(`Replaced with ${newGroups.length} placement${newGroups.length > 1 ? "s" : ""}`);
    } else {
      setJob({ ...job, artwork_groups: [...job.artwork_groups, ...newGroups] });
      toast.success(`Added ${newGroups.length} placement${newGroups.length > 1 ? "s" : ""}`);
    }
    
    setShowTemplateConfirm(false);
    setSelectedTemplate("Custom");
  };

  const handleApplyTemplate = () => {
    if (selectedTemplate === "Custom") {
      toast.info("Select a template first");
      return;
    }
    
    if (job.artwork_groups.length > 0) {
      setShowTemplateConfirm(true);
    } else {
      applyPlacementTemplate("add");
    }
  };

  // Supplier Shipping handlers
  const addSupplierShipping = () => {
    setHasUnsavedChanges(true);
    setJob({
      ...job,
      supplier_shipping: [
        ...job.supplier_shipping,
        { id: crypto.randomUUID(), description: "Apparel Shipping", cost: 0 },
      ],
    });
  };

  const updateSupplierShipping = (id, field, value) => {
    setHasUnsavedChanges(true);
    setJob({
      ...job,
      supplier_shipping: job.supplier_shipping.map((s) =>
        s.id === id ? { ...s, [field]: value } : s
      ),
    });
  };

  const removeSupplierShipping = (id) => {
    setHasUnsavedChanges(true);
    setJob({
      ...job,
      supplier_shipping: job.supplier_shipping.filter((s) => s.id !== id),
    });
  };

  const totalGarments = job.garments.reduce((sum, g) => sum + (g.quantity || 0), 0);
  const totalSupplierShipping = job.supplier_shipping.reduce((sum, s) => sum + (s.cost || 0), 0);

  // Determine page title based on job state
  const pageTitle = jobNumber 
    ? `${jobNumber} – ${job.job_name || (job.client_type === "Retail" ? "Retail Sale" : "Quote")}`
    : job.client_type === "Retail" 
      ? "New Retail Sale" 
      : "New Business Quote";

  // Format last saved time
  const formatLastSaved = () => {
    if (!lastSaved) return null;
    const now = new Date();
    const diff = now - lastSaved;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "Just now";
    if (minutes === 1) return "1 min ago";
    if (minutes < 60) return `${minutes} mins ago`;
    return lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // TopBar actions - prioritized: Save, Send Quote, Download PDF
  const _latestSnap = snapshots.length ? snapshots[snapshots.length - 1] : null;
  let versionBadge = null;
  if (jobId && !readOnlyVersion) {
    if (hasUnsavedChanges) {
      versionBadge = { text: "Unsaved changes", cls: "bg-orange-100 text-orange-700 border-orange-200" };
    } else if (_latestSnap && _latestSnap.is_manual_version) {
      versionBadge = { text: `V${_latestSnap.version} saved · ready to send`, cls: "bg-green-100 text-green-700 border-green-200" };
    } else if (job.status === "Sent" && _latestSnap) {
      versionBadge = { text: `V${_latestSnap.version} sent`, cls: "bg-gray-100 text-gray-600 border-gray-200" };
    }
  }
  const topBarActions = (
    <div className="flex items-center gap-2">
      {/* Business / Retail job-level toggle — drives pricing source + summary */}
      <div
        className="flex items-center rounded-md border border-slate-200 overflow-hidden mr-1"
        data-testid="header-client-type-toggle"
      >
        <button
          type="button"
          onClick={() => handleClientTypeChange("Business")}
          data-testid="header-toggle-business"
          className={`text-xs font-semibold px-3 py-1.5 transition-colors ${
            job.client_type === "Retail"
              ? "bg-white text-slate-600 hover:bg-slate-50"
              : "bg-aa-primary text-white"
          }`}
        >
          Business
        </button>
        <button
          type="button"
          onClick={() => handleClientTypeChange("Retail")}
          data-testid="header-toggle-retail"
          className={`text-xs font-semibold px-3 py-1.5 transition-colors ${
            job.client_type === "Retail"
              ? "bg-orange-500 text-white"
              : "bg-white text-slate-600 hover:bg-slate-50"
          }`}
        >
          Retail
        </button>
      </div>

      {/* Version status indicator */}
      {versionBadge && (
        <span
          data-testid="version-status-badge"
          className={`text-xs font-medium px-2 py-1 rounded-md border hidden sm:inline ${versionBadge.cls}`}
        >
          {versionBadge.text}
        </span>
      )}

      {/* Save Status Indicator */}
      {lastSaved && !hasUnsavedChanges && (
        <span className="text-xs text-workspace-text-muted hidden sm:inline">
          Saved {formatLastSaved()}
        </span>
      )}
      
      {/* Save Button - Primary */}
      <Button 
        onClick={saveJob} 
        disabled={isSaving}
        data-testid="save-job-btn"
        size="sm"
        className={`${
          hasUnsavedChanges 
            ? "bg-orange-500 hover:bg-orange-600" 
            : "bg-aa-primary hover:bg-aa-primary/90"
        }`}
      >
        <Save className="w-4 h-4 mr-1.5" />
        {isSaving ? "Saving..." : hasUnsavedChanges ? "Save*" : "Save"}
      </Button>
      
      {/* Send Quote - Visible when job saved and Gmail connected */}
      {jobId && gmailConnected && !isApproved && (
        <Button 
          onClick={handleSendQuoteClick}
          data-testid="send-quote-btn"
          variant="outline"
          size="sm"
        >
          <Mail className="w-4 h-4 mr-1.5" />
          Send Quote
        </Button>
      )}
      
      {/* Download PDF */}
      {(quote || hasApprovedSnapshot) && (
        <Button 
          onClick={downloadPDF}
          data-testid="download-pdf-btn"
          variant="outline"
          size="sm"
        >
          <FileDown className="w-4 h-4 mr-1.5" />
          PDF
        </Button>
      )}
      
      {/* Download Quantity Break Comparison PDF - Only in QTY BREAK mode */}
      {job.is_quantity_break && jobId && (
        <Button 
          onClick={downloadQuantityBreakPDF}
          data-testid="download-qty-break-pdf-btn"
          variant="outline"
          size="sm"
          className="border-purple-300 text-purple-700 hover:bg-purple-50"
        >
          <Layers className="w-4 h-4 mr-1.5" />
          Comparison PDF
        </Button>
      )}
      
      {/* Download Individual Quote PDF for selected column - Only in QTY BREAK mode */}
      {job.is_quantity_break && jobId && job.quantity_break_settings?.length > 0 && (
        <Button 
          onClick={downloadIndividualQuotePDF}
          data-testid="download-individual-pdf-btn"
          variant="outline"
          size="sm"
          className="border-blue-300 text-blue-700 hover:bg-blue-50"
        >
          <FileDown className="w-4 h-4 mr-1.5" />
          {job.quantity_break_settings[selectedQtyColumn]?.quantity || "?"} Units PDF
        </Button>
      )}
      
      {/* Approve - When applicable */}
      {jobId && !hasApprovedSnapshot && (
        <Button
          onClick={() => createSnapshot(false)}
          disabled={isCreatingSnapshot || isSaving}
          data-testid="mark-approved-btn"
          size="sm"
          className="bg-aa-success hover:bg-aa-success/90"
        >
          <CheckCircle2 className="w-4 h-4 mr-1.5" />
          {isCreatingSnapshot ? "..." : "Approve"}
        </Button>
      )}
      
      {/* Revision - When approved and has changes */}
      {jobId && hasApprovedSnapshot && hasUnsavedChanges && (
        <Button
          onClick={() => createSnapshot(true)}
          disabled={isCreatingSnapshot || isSaving}
          data-testid="create-revision-btn"
          size="sm"
          className="bg-aa-warning hover:bg-aa-warning/90"
        >
          <History className="w-4 h-4 mr-1.5" />
          Revision
        </Button>
      )}
      
      {/* More Actions Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" data-testid="more-actions-btn">
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52 bg-white border border-workspace-border">
          {/* New Job */}
          <DropdownMenuItem onClick={startNewJob} data-testid="new-job-item">
            <FilePlus className="w-4 h-4 mr-2" />
            New {job.client_type === "Retail" ? "Retail Sale" : "Quote"}
          </DropdownMenuItem>
          
          {/* Duplicate */}
          {jobId && (
            <DropdownMenuItem 
              onClick={duplicateJob}
              disabled={isDuplicating}
              data-testid="duplicate-job-item"
            >
              <Copy className="w-4 h-4 mr-2" />
              {isDuplicating ? "Duplicating..." : "Duplicate as Reorder"}
            </DropdownMenuItem>
          )}
          
          <DropdownMenuSeparator />
          
          {/* Templates */}
          <DropdownMenuItem onClick={openApplyTemplate} data-testid="apply-template-item">
            <FolderOpen className="w-4 h-4 mr-2" />
            Apply Template
          </DropdownMenuItem>
          
          {jobId && (job.garments?.length > 0 || job.artwork_groups?.length > 0) && (
            <DropdownMenuItem onClick={openSaveAsTemplate} data-testid="save-as-template-item">
              <FileStack className="w-4 h-4 mr-2" />
              Save as Template
            </DropdownMenuItem>
          )}
          
          <DropdownMenuSeparator />
          
          {/* Production Sheet */}
          {job.status !== "Draft" && (
            <DropdownMenuItem onClick={downloadProductionSheet} data-testid="production-sheet-item">
              <ClipboardList className="w-4 h-4 mr-2" />
              Production Sheet
            </DropdownMenuItem>
          )}
          
          {/* Xero Invoice */}
          {jobId && xeroConnected && hasApprovedSnapshot && !xeroInvoice && (
            <DropdownMenuItem 
              onClick={() => createXeroInvoice(false)}
              disabled={isCreatingXeroInvoice}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {isCreatingXeroInvoice ? "Creating..." : "Create Xero Invoice"}
            </DropdownMenuItem>
          )}
          
          {/* Mark as Sent (manual) */}
          {jobId && job.status === "Draft" && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={markAsSent} disabled={isSendingQuote} data-testid="mark-sent-item">
                <Check className="w-4 h-4 mr-2" />
                Mark as Sent (manual)
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  // Determine accent color based on client type
  const isRetail = job.client_type === "Retail";

  return (
    <WorkspaceLayout title={pageTitle} actions={topBarActions}>
      {/* Status Bar - Approval/Quote Status Info */}
      {(approvalInfo || quoteSentInfo || xeroInvoice) && (
        <div className={`border-b px-6 py-2 text-sm ${
          isRetail ? "bg-orange-50 border-orange-200" : "bg-blue-50 border-blue-200"
        }`}>
          <div className="flex items-center gap-4 flex-wrap">
            {approvalInfo && (
              <span className="flex items-center gap-1.5 text-green-700">
                <CheckCircle2 className="w-4 h-4" />
                Approved: {approvalInfo.approved_at ? new Date(approvalInfo.approved_at).toLocaleDateString() : ""}
                {approvalInfo.revision_number > 1 && ` (Rev ${approvalInfo.revision_number})`}
              </span>
            )}
            {quoteSentInfo && (
              <span className="flex items-center gap-1.5 text-blue-700">
                <Mail className="w-4 h-4" />
                Quote sent to {quoteSentInfo.client_email}
              </span>
            )}
            {xeroInvoice && (
              <a 
                href={xeroInvoice.invoice_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-purple-700 hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Xero: {xeroInvoice.invoice_number}
              </a>
            )}
          </div>
        </div>
      )}

      {/* ==================== READ-ONLY VERSION BANNER (#4) ==================== */}
      {readOnlyVersion && (
        <div
          className="border-b border-amber-300 bg-amber-100 px-6 py-3 text-sm flex items-center justify-between flex-wrap gap-2"
          data-testid="readonly-version-banner"
        >
          <span className="flex items-center gap-2 text-amber-900 font-medium">
            <Lock className="w-4 h-4" />
            Viewing V{readOnlyVersion} — read only. Edits and saving are disabled.
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-amber-400 text-amber-900 hover:bg-amber-200"
              onClick={() => handleRevertVersion(readOnlyVersion)}
              data-testid="banner-revert-version-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1.5" />
              Revert to V{readOnlyVersion}
            </Button>
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={exitReadOnly}
              data-testid="banner-back-to-current-btn"
            >
              Back to current
            </Button>
          </div>
        </div>
      )}

      {/* ==================== MAIN CONTENT ==================== */}
      <main className="p-6 pb-48 sm:pb-44 lg:pb-40" style={{ backgroundColor: isRetail ? '#faf8f6' : '#f8f9fb' }}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Inputs */}
          <div className="lg:col-span-2 space-y-6">
            {/* ==================== VERSION HISTORY PANEL (#4) ==================== */}
            <VersionHistoryPanel
              jobId={jobId}
              snapshots={snapshots}
              readOnlyVersion={readOnlyVersion}
              savingVersion={savingVersion}
              onSaveVersion={handleSaveVersion}
              onRevertVersion={handleRevertVersion}
              onViewVersion={openVersionInNewTab}
              formatCurrency={formatCurrency}
            />

            {/* Job Details - Collapsible */}
            <JobDetailsCard
              job={job}
              setJob={setJob}
              setHasUnsavedChanges={setHasUnsavedChanges}
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              deadlineCountdown={deadlineCountdown}
              isAdmin={isAdmin}
              enableQuantityBreakMode={enableQuantityBreakMode}
              showClientDetails={showClientDetails}
              setShowClientDetails={setShowClientDetails}
              updateClient={updateClient}
              onConvertToTask={convertToTask}
            />

            {/* Retail / Design Notes - Show prominently when present */}
            {job.retail_design_notes && job.retail_design_notes.trim() && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="text-sm font-medium text-amber-800 flex items-center gap-2 mb-2">
                  <StickyNote className="w-4 h-4" />
                  Retail / Design Notes
                </h4>
                <p className="text-sm text-amber-900 whitespace-pre-wrap">{job.retail_design_notes}</p>
              </div>
            )}

            {/* Quantity Break Scenarios Panel - Only visible in QTY BREAK mode */}
            {job.is_quantity_break && (
              <QuantityBreakPanel
                settings={job.quantity_break_settings || []}
                onSettingsChange={(newSettings) => {
                  setHasUnsavedChanges(true);
                  setJob({ ...job, quantity_break_settings: newSettings });
                }}
                results={quantityBreakResults?.results || []}
                selectedColumn={selectedQtyColumn}
                onSelectColumn={setSelectedQtyColumn}
                isCalculating={isCalculatingQtyBreak}
                showInternal={showInternal}
              />
            )}

            {/* Garments Section - Collapsible (hidden for Task-type cards) */}
            {job.job_type !== "task" && (
            <GarmentsCard
              job={job}
              setJob={setJob}
              setHasUnsavedChanges={setHasUnsavedChanges}
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              totalGarments={totalGarments}
              isAdmin={isAdmin}
              addGarment={addGarment}
              quickAddSearch={quickAddSearch}
              setQuickAddSearch={setQuickAddSearch}
              searchQuickAdd={searchQuickAdd}
              quickAddResults={quickAddResults}
              selectQuickAddProduct={selectQuickAddProduct}
              isSearchingQuickAdd={isSearchingQuickAdd}
              quickAddSearchDone={quickAddSearchDone}
              quickAddProduct={quickAddProduct}
              quickAddColour={quickAddColour}
              setQuickAddColour={setQuickAddColour}
              quickAddQty={quickAddQty}
              setQuickAddQty={setQuickAddQty}
              addQuickGarment={addQuickGarment}
              lookupColour={lookupColour}
              openProductPicker={openProductPicker}
              updateGarment={updateGarment}
              updateGarmentFields={updateGarmentFields}
              searchAutocomplete={searchAutocomplete}
              closeAutocomplete={closeAutocomplete}
              autocompleteGarmentId={autocompleteGarmentId}
              autocompleteResults={autocompleteResults}
              selectAutocompleteProduct={selectAutocompleteProduct}
              autocompleteSearchDone={autocompleteSearchDone}
              isSearchingAutocomplete={isSearchingAutocomplete}
              duplicateGarment={duplicateGarment}
              removeGarment={removeGarment}
            />
            )}

            {/* Artwork Groups Section - Collapsible (hidden for Task-type cards) */}
            {job.job_type !== "task" && (
            <PrintGroupsCard
              job={job}
              setJob={setJob}
              setHasUnsavedChanges={setHasUnsavedChanges}
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
              PLACEMENT_TEMPLATES={PLACEMENT_TEMPLATES}
              handleApplyTemplate={handleApplyTemplate}
              addArtworkGroup={addArtworkGroup}
              showTemplateConfirm={showTemplateConfirm}
              setShowTemplateConfirm={setShowTemplateConfirm}
              applyPlacementTemplate={applyPlacementTemplate}
              jobId={jobId}
              jobNumber={jobNumber}
              updateArtworkGroup={updateArtworkGroup}
              updateArtworkGroupFields={updateArtworkGroupFields}
              updateArtworkAllocation={updateArtworkAllocation}
              removeArtworkGroup={removeArtworkGroup}
              quote={quote}
              isAdmin={isAdmin}
              showInternal={showInternal}
            />
            )}

            {/* Dynamic Method Comparison Panels — any source method vs any target method */}
            {(quote?.dynamic_comparisons || []).map((cmp, ci) => (
              <MethodComparisonPanel
                key={`${cmp.target_method}-${ci}`}
                comparison={cmp}
                inputs={job.comparison_inputs || { garments_per_sheet: 1, colour_count: 1, light_on_dark: false, custom_colour_count: 0, cuts_per_sheet: 0, sheet_size: "Standard" }}
                onInputsChange={(next) => setJob(prev => ({ ...prev, comparison_inputs: next }))}
                targetConfig={job.comparison_target_config || { print_method: cmp.target_method, screens: 1, prints_per_sheet: 1, sheet_size: "Standard", print_colour: "Other", tsp_colours: 1, tsp_selected_charges: ["Film", "Screen"], stitch_count: 9999 }}
                onTargetChange={(next) => setJob(prev => ({ ...prev, comparison_target_config: next }))}
                onUse={() => {
                  const target = cmp.target_method;
                  const ids = cmp.included_group_ids || [];
                  const cfg = job.comparison_target_config || {};
                  const included = job.artwork_groups.filter(a => ids.includes(a.id));
                  const isSheet = target === "Screen Print Pro" || target === "UltraColour Reflective";
                  setHasUnsavedChanges(true);
                  if (isSheet) {
                    const names = included.map(g => g.placement_name || g.name).filter(Boolean).join(" + ");
                    const allocations = [];
                    included.forEach(group => (group.allocations || []).forEach(alloc => {
                      const ex = allocations.find(a => a.garment_id === alloc.garment_id);
                      if (ex) ex.placement_count = Math.max(ex.placement_count, alloc.placement_count);
                      else allocations.push({ ...alloc });
                    }));
                    const newGroup = {
                      id: `cmp-${Date.now()}`,
                      print_method: target,
                      placement_name: names || target,
                      size_tier: "",
                      allocations,
                      screens: cfg.screens || 1,
                      print_colour: cfg.print_colour || "Other",
                      light_on_dark: cfg.light_on_dark || false,
                      prints_per_sheet: cfg.prints_per_sheet || 1,
                      custom_colour_count: cfg.custom_colour_count || 0,
                      cuts_per_sheet: cfg.cuts_per_sheet || 0,
                      sheet_size: cfg.sheet_size || "Standard",
                    };
                    setJob(prev => ({ ...prev, artwork_groups: [...prev.artwork_groups.filter(a => !ids.includes(a.id)), newGroup], comparison_target_config: null }));
                  } else {
                    setJob(prev => ({
                      ...prev,
                      artwork_groups: prev.artwork_groups.map(a => ids.includes(a.id) ? {
                        ...a,
                        print_method: target,
                        comparison_enabled: false,
                        comparison_target: null,
                        size_tier: cfg.size_tier || a.size_tier || "M",
                        blocker: cfg.blocker || a.blocker || "Standard",
                        stitch_count: cfg.stitch_count || a.stitch_count || 5000,
                        tsp_colours: cfg.tsp_colours || a.tsp_colours || 1,
                        tsp_selected_charges: cfg.tsp_selected_charges || a.tsp_selected_charges,
                        tsp_charge_quantities: cfg.tsp_charge_quantities || a.tsp_charge_quantities,
                      } : a),
                      comparison_target_config: null,
                    }));
                  }
                  toast.success(`Converted to ${target}`);
                }}
              />
            ))}

            {/* Supplier Shipping Section - Collapsible - Lighter styling (hidden for Task-type cards) */}
            {job.job_type !== "task" && (
            <SupplierShippingCard
              job={job}
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              totalSupplierShipping={totalSupplierShipping}
              formatCurrency={formatCurrency}
              addSupplierShipping={addSupplierShipping}
              updateSupplierShipping={updateSupplierShipping}
              removeSupplierShipping={removeSupplierShipping}
            />
            )}
          </div>

          {/* Right Column - Quote Summary - More Prominent */}
          <div className="lg:col-span-1">
            <div className="sticky-summary space-y-4">
              <QuoteSummaryPanel
                job={job}
                setJob={setJob}
                setHasUnsavedChanges={setHasUnsavedChanges}
                quote={quote}
                quantityBreakResults={quantityBreakResults}
                selectedQtyColumn={selectedQtyColumn}
                isCalculating={isCalculating}
                isCalculatingQtyBreak={isCalculatingQtyBreak}
                isAdmin={isAdmin}
                showInternal={showInternal}
                setShowInternal={setShowInternal}
                showDesignFeeToClient={showDesignFeeToClient}
                setShowDesignFeeToClient={setShowDesignFeeToClient}
                settings={settings}
                updateGarmentOverrides={updateGarmentOverrides}
                hasApprovedSnapshot={hasApprovedSnapshot}
                latestSnapshot={latestSnapshot}
                snapshots={snapshots}
                showSnapshotHistory={showSnapshotHistory}
                setShowSnapshotHistory={setShowSnapshotHistory}
                selectedSnapshot={selectedSnapshot}
                setSelectedSnapshot={setSelectedSnapshot}
              />
              
              {/* Communication Timeline - Only for saved jobs */}
              {jobId && (
                <div className="mt-4">
                  <JobCommunicationTimeline 
                    jobId={jobId}
                    jobName={job.job_name}
                    customerEmail={job.client?.email || job.billing_email}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Sticky Summary Bar - Responsive */}
      {(() => {
        // Compute display data - use quantity break column data when in QTY BREAK mode
        const displayQuote = job.is_quantity_break && quantityBreakResults?.results?.[selectedQtyColumn]
          ? quantityBreakResults.results[selectedQtyColumn]
          : quote;
        const displayTotalGarments = displayQuote?.total_garments ?? totalGarments;
        const displaySellTotal = displayQuote?.production_total_ex_gst ?? displayQuote?.subtotal_ex_gst ?? 0;
        const displayMarginDollars = displayQuote?.margin_dollars ?? 0;
        const displayMarginPercent = displayQuote?.margin_percent ?? displayQuote?.actual_margin_percent ?? 0;
        const displayEstimatedHours = displayQuote?.estimated_hours ?? 0;
        const displayMarginPerHour = displayQuote?.margin_per_hour ?? 0;
        const displayTotalIncGst = displayQuote?.total_inc_gst ?? 0;
        
        return (
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-40" data-testid="sticky-summary-bar">
        <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between overflow-x-hidden">
          {/* Left side: Core info */}
          <div className="flex items-center gap-2 sm:gap-4 lg:gap-6 text-xs sm:text-sm min-w-0">
            {/* Quantity Break Column Indicator */}
            {job.is_quantity_break && quantityBreakResults?.results?.[selectedQtyColumn] && (
              <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-xs shrink-0">
                {quantityBreakResults.results[selectedQtyColumn].quantity} units
              </Badge>
            )}
            
            {/* Total Garments */}
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <Shirt className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-500" />
              <span className="font-medium">{displayTotalGarments}</span>
              <span className="hidden sm:inline">garments</span>
            </div>
            
            {/* Sell Total */}
            <div className="hidden sm:flex items-center gap-1.5 sm:gap-2 shrink-0">
              <span className="text-slate-500">Sell Total:</span>
              <span className="font-medium">${displaySellTotal?.toFixed(2) || "0.00"}</span>
            </div>
          </div>
          
          {/* Right side: Margin info + Total */}
          <div className="flex items-center gap-2 sm:gap-4 lg:gap-6 shrink-0">
            {/* Admin: Margin Metrics with Tooltips */}
            {isAdmin && displayQuote && (
              <TooltipProvider delayDuration={200}>
                {/* Margin $ */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="hidden md:flex items-center gap-1 text-xs sm:text-sm text-green-700 cursor-help">
                      <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="font-medium">${(displayMarginDollars || 0).toFixed(0)}</span>
                      <Info className="w-3 h-3 text-green-500/60" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[220px] text-center">
                    <p className="font-semibold mb-1">Margin $</p>
                    <p>Total profit from this job (sell total minus true cost)</p>
                  </TooltipContent>
                </Tooltip>
                
                {/* Margin % */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="hidden lg:flex items-center gap-1 text-xs sm:text-sm text-slate-600 cursor-help">
                      <Percent className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span>{(displayMarginPercent || 0).toFixed(0)}%</span>
                      <Info className="w-3 h-3 text-slate-400" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[220px] text-center">
                    <p className="font-semibold mb-1">Margin %</p>
                    <p>Total job margin as a percentage of sell price</p>
                  </TooltipContent>
                </Tooltip>
                
                {/* Est. Hours */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="hidden lg:flex items-center gap-1 text-xs sm:text-sm text-slate-600 cursor-help">
                      <Timer className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span>{(displayEstimatedHours || 0).toFixed(1)}h</span>
                      <Info className="w-3 h-3 text-slate-400" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[250px] text-center">
                    <p className="font-semibold mb-1">Est. Hours</p>
                    <p>Estimated production time based on total prints and configured production rate</p>
                  </TooltipContent>
                </Tooltip>
                
                {/* Margin / hr - with conditional red color */}
                {(() => {
                  const marginPerHour = displayMarginPerHour || 0;
                  const labourRate = settings?.labour_rate || 75;
                  const isBelowTarget = marginPerHour < labourRate;
                  
                  return (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className={`hidden xl:flex items-center gap-1 text-xs sm:text-sm cursor-help ${isBelowTarget ? 'text-red-600' : 'text-slate-600'}`}>
                          <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          <span className="font-medium">${marginPerHour.toFixed(0)}/hr</span>
                          <Info className={`w-3 h-3 ${isBelowTarget ? 'text-red-400' : 'text-slate-400'}`} />
                          {isBelowTarget && <AlertTriangle className="w-3 h-3 text-red-500" />}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className={`max-w-[280px] text-center ${isBelowTarget ? 'bg-red-600' : ''}`}>
                        <p className="font-semibold mb-1">Margin / hr</p>
                        <p>Margin per hour based on total job margin divided by estimated production time</p>
                        {isBelowTarget && (
                          <p className="mt-2 font-medium text-yellow-200">⚠️ Below target rate of ${labourRate}/hr</p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  );
                })()}
              </TooltipProvider>
            )}
            
            {/* Total - always visible */}
            <div className="flex items-center gap-1.5 sm:gap-2 bg-green-50 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg">
              <span className="text-green-700 font-medium text-xs sm:text-sm">TOTAL:</span>
              <span className="text-sm sm:text-lg font-bold text-green-700">
                ${displayTotalIncGst?.toFixed(2) || "0.00"}
              </span>
            </div>
          </div>
        </div>
      </div>
        );
      })()}

      {/* Product Picker Modal */}
      <Dialog open={showProductPicker} onOpenChange={setShowProductPicker}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Library className="w-5 h-5" />
              Select from Product Library
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Search */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search products..."
                  value={productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    loadProducts(e.target.value);
                  }}
                  className="pl-10"
                  data-testid="product-search-input"
                />
              </div>
            </div>

            {/* Products List */}
            <ScrollArea className="h-80">
              {isLoadingProducts ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : products.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No products found</p>
                  <Link to="/products" className="text-indigo-600 text-xs hover:underline">
                    Add products to library
                  </Link>
                </div>
              ) : (
                <div className="space-y-2 pr-4">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className="p-3 border rounded-lg hover:bg-secondary/50 cursor-pointer transition-colors"
                      onClick={() => selectProduct(product)}
                      data-testid={`picker-product-${product.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs font-mono">
                              {product.style_code || "—"}
                            </Badge>
                            <span className="font-medium truncate">
                              {product.brand} {product.product_name}
                            </span>
                          </div>
                          {product.available_colours?.length > 0 && (
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {product.available_colours.slice(0, 4).map((c) => {
                                const colourData = lookupColour(c, product.supplier);
                                return (
                                  <Badge key={c} variant="secondary" className="text-xs flex items-center gap-1">
                                    <ColourSwatch hex={colourData?.hex} size="xs" />
                                    {c}
                                  </Badge>
                                );
                              })}
                              {product.available_colours.length > 4 && (
                                <Badge variant="secondary" className="text-xs">
                                  +{product.available_colours.length - 4}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                        {isAdmin && (
                          <span className="font-mono text-green-700 font-medium ml-3">
                            ${parseFloat((job.client_type === "Retail" ? (product.retail_price || product.default_cost) : product.default_cost) || 0).toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            <div className="text-xs text-muted-foreground text-center">
              <Link to="/products" className="text-indigo-600 hover:underline">
                Manage Product Library
              </Link>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save as Template Modal */}
      <Dialog open={showSaveAsTemplateModal} onOpenChange={setShowSaveAsTemplateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileStack className="w-5 h-5" />
              Save as Template
            </DialogTitle>
            <DialogDescription>
              Create a reusable template from this quote's configuration.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                placeholder="e.g., Club Uniform Package"
                value={saveTemplateData.name}
                onChange={(e) => setSaveTemplateData(prev => ({ ...prev, name: e.target.value }))}
                data-testid="save-template-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-desc">Description</Label>
              <Input
                id="template-desc"
                placeholder="Brief description"
                value={saveTemplateData.description}
                onChange={(e) => setSaveTemplateData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={saveTemplateData.category}
                onValueChange={(v) => setSaveTemplateData(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger data-testid="save-template-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="General">General</SelectItem>
                  <SelectItem value="Club">Club</SelectItem>
                  <SelectItem value="Business">Business</SelectItem>
                  <SelectItem value="Retail">Retail</SelectItem>
                  <SelectItem value="Event">Event</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Preview what will be saved */}
            <div className="p-3 bg-gray-50 rounded-lg text-sm">
              <p className="font-medium text-gray-700 mb-2">Will save:</p>
              <ul className="text-gray-600 space-y-1">
                <li className="flex items-center gap-2">
                  <Shirt className="w-3 h-3" />
                  {job.garments.length} garment(s)
                </li>
                <li className="flex items-center gap-2">
                  <Palette className="w-3 h-3" />
                  {job.artwork_groups.length} print group(s)
                </li>
                <li className="flex items-center gap-2">
                  <User className="w-3 h-3" />
                  Client type: {job.client_type}
                </li>
                {job.margin_percent && (
                  <li className="flex items-center gap-2">
                    <DollarSign className="w-3 h-3" />
                    Margin: {(job.margin_percent * 100).toFixed(0)}%
                  </li>
                )}
              </ul>
              <p className="text-xs text-gray-400 mt-2">
                Customer info, status, and pricing will NOT be saved.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveAsTemplateModal(false)}>
              Cancel
            </Button>
            <Button onClick={saveAsTemplate} disabled={isSavingTemplate} data-testid="confirm-save-template">
              {isSavingTemplate ? "Saving..." : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Template Modal */}
      <Dialog open={showApplyTemplateModal} onOpenChange={setShowApplyTemplateModal}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              Apply Template
            </DialogTitle>
            <DialogDescription>
              Select a template to pre-populate this quote. Customer info will be preserved.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <ScrollArea className="h-80">
              {templatesLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading templates...</div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileStack className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No templates yet</p>
                  <p className="text-xs mt-1">
                    Save a quote as a template to get started.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 pr-4">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className="p-3 border rounded-lg hover:bg-secondary/50 cursor-pointer transition-colors"
                      onClick={() => applyTemplate(template)}
                      data-testid={`apply-template-${template.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{template.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {template.category}
                            </Badge>
                          </div>
                          {template.description && (
                            <p className="text-sm text-gray-500 truncate">{template.description}</p>
                          )}
                          <div className="flex gap-3 mt-1 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Shirt className="w-3 h-3" />
                              {template.garments?.length || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <Palette className="w-3 h-3" />
                              {template.artwork_groups?.length || 0}
                            </span>
                            {template.use_count > 0 && (
                              <span>Used {template.use_count}x</span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            <div className="text-xs text-muted-foreground text-center mt-4">
              <Link to="/settings/templates" className="text-indigo-600 hover:underline">
                Manage Templates
              </Link>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Quote Confirmation Dialog */}
      <Dialog open={showSendConfirm} onOpenChange={setShowSendConfirm}>
        <DialogContent className="max-w-md" data-testid="send-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Ready to send V{getSendableVersion()}?
            </DialogTitle>
            <DialogDescription>
              You're about to send <strong>V{getSendableVersion()}</strong> to{" "}
              <strong>{job.client?.email || "the client"}</strong>. This locks V{getSendableVersion()} as the
              version your customer sees. Repeat saves after this create a new version.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Label htmlFor="send-change-note" className="text-sm text-gray-600">
              What changed in this version? (optional — for your records)
            </Label>
            <textarea
              id="send-change-note"
              data-testid="send-change-note-input"
              value={sendChangeNote}
              onChange={(e) => setSendChangeNote(e.target.value)}
              rows={3}
              placeholder="e.g. Updated garment quantities and adjusted pricing"
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30 resize-none"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSendConfirm(false)}
              data-testid="send-confirm-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="bg-aa-primary hover:bg-aa-primary/90"
              data-testid="send-confirm-continue-btn"
              onClick={() => {
                setShowSendConfirm(false);
                setShowSendQuoteEmail(true);
              }}
            >
              <Mail className="w-4 h-4 mr-1.5" />
              Continue to Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Quote Email Modal */}
      <SendQuoteEmailModal
        open={showSendQuoteEmail}
        onClose={() => setShowSendQuoteEmail(false)}
        accounts={gmailAccounts}
        jobName={job.job_name}
        jobNumber={jobNumber}
        customerEmail={job.client?.email || job.billing_email}
        customerName={job.client?.name || job.billing_name}
        isQuantityBreak={job.is_quantity_break}
        onSend={sendQuoteViaEmail}
      />
    </WorkspaceLayout>
  );
}
